import React from 'react';
import {
  Alert, Box, Typography, useTheme,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import type { UserCoupon } from '../../queries/coupons';

const formatCLP = (amount: number): string => `$${Math.round(amount).toLocaleString('es-CL')}`;

export function couponDiscountHeadline(coupon: UserCoupon): string {
  if (coupon.discountType === 'PERCENTAGE') {
    return `${coupon.discountValue}% de descuento`;
  }
  return `${formatCLP(coupon.discountValue)} de descuento`;
}

function formatLongDate(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' });
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2" color="primary.main" fontWeight="bold" sx={{ textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
}

export default function CouponDetailContent({ coupon }: { coupon: UserCoupon }) {
  const theme = useTheme();
  const expiry = formatLongDate(coupon.expiresAt);
  const discountValueText = coupon.discountType === 'PERCENTAGE'
    ? `${coupon.discountValue}% (hasta ${formatCLP(coupon.maxDiscountAmount)})`
    : formatCLP(coupon.discountValue);
  const included = coupon.includedPartnerNames ?? [];
  const excluded = coupon.excludedPartnerNames ?? [];

  return (
    <Box sx={{ textAlign: 'left', marginTop: 2, marginBottom: -2.5 }}>
      <DetailRow label="Descuento" value={discountValueText} />
      {coupon.minPurchaseAmount != null && coupon.minPurchaseAmount > 0 && (
        <DetailRow label="Compra mínima" value={formatCLP(coupon.minPurchaseAmount)} />
      )}
      {included.length > 0 && (
        <DetailRow label="Válido solo en" value={included.join(', ')} />
      )}
      {excluded.length > 0 && (
        <DetailRow label="No válido en" value={excluded.join(', ')} />
      )}
      {expiry && <DetailRow label="Válido hasta" value={expiry} />}

      <Box sx={{
        display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, alignItems: 'center',
      }}
      >
        <Typography variant="body2">Código</Typography>
        <Box
          component="span"
          sx={{
            backgroundColor: 'tertiary.main',
            color: 'primary.main',
            fontSize: 12,
            fontWeight: 800,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'primary.main',
            px: 1,
            py: '2px',
            letterSpacing: 0.5,
          }}
        >
          {coupon.code}
        </Box>
      </Box>

      {coupon.status === 'RESERVED' && (
        <Alert
          icon={<ScheduleIcon fontSize="small" />}
          sx={{
            marginTop: 2.5, borderRadius: 2, alignItems: 'flex-start', fontSize: '12px', backgroundColor: '#F5F3FA', border: `1px solid ${theme.palette.grey[300]}`, marginInline: 0, width: '100%',
          }}
        >
          Este cupón está en uso en una compra que quedó en proceso. Si no la completas,
          volverá a estar disponible en unos 15 minutos.
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 7.5 }}>
        *El descuento se aplica automáticamente al pagar con
        banca.me cuando cumples las condiciones.
      </Typography>
      {coupon.status !== 'RESERVED' && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        *Cuando un cupón se uso en una compra que quedó en proceso (no fue completada),
        volverá a estar disponible en unos 15 minutos.
      </Typography>
      )}
    </Box>
  );
}
